import constants from "../Scroller/Constants";
import PinchZoom from "./pinch-zoom";
import { PinchZoomAnimationEvent } from "./pinch-zoom";
import { AbstractGallery } from "../Gallery";
import { scrollerNavBeforeResultDetail } from "../Scroller/ScrollerNav";
import { preventScroll, releaseScroll } from "../../Utils/PreventScroll";

export class FullscreenGallery {
    private readonly parent: HTMLElement;
    private nextSibling: ChildNode;
    private readonly btnClose: HTMLSpanElement;
    private readonly scroller: HTMLElement;
    private readonly thumbScroller: HTMLElement;
    private zoomHandlers: PinchZoom[] = [];
    private abortController: AbortController;
    private readonly node: HTMLElement;
    private readonly imageZoomedHandler: () => void;
    private readonly galleryNavHandler: () => void;
    private readonly resizeHandler: () => void;

    constructor(private readonly gallery: AbstractGallery) {
        this.gallery = gallery;
        this.node = gallery.node;
        this.scroller = this.node.querySelector(constants.SELECTORS.SCROLLER);

        const track = this.node.querySelector<HTMLElement>(constants.SELECTORS.TRACK);
        this.thumbScroller = track?.querySelector<HTMLElement>(constants.SELECTORS.SCROLLER);

        this.parent = this.node.parentElement;
        this.btnClose = this.node.querySelector<HTMLSpanElement>(constants.SELECTORS.BTN_CLOSE);
        this.imageZoomedHandler = this.handleImageZoomed.bind(this);
        this.galleryNavHandler = this.handleGalleryNav.bind(this);
        this.resizeHandler = this.handleResize.bind(this);
    }

    /**
     * show fullscreen gallery
     *
     * this function has to be invoked by mouseup event to be able
     * to stop propagation to mouseup handler of pinchzoom
     * @param event
     */
    public show(event: MouseEvent): void {
        if (!this.isFullscreen) {
            event.stopPropagation();
            preventScroll();
            this.gallery.images.forEach((img) => this.zoomHandlers.push(new PinchZoom(img)));
            const href = location.href.replace(/#.*$/, "");
            history.pushState({}, "", href + constants.FULLSCREEN_URL_HASH);
            this.viewportZoomable = false;
            this.nextSibling = this.node.nextSibling;
            document.body.appendChild(this.node);
            this.scroller?.dispatchEvent(
                new CustomEvent(constants.EVENTS.SCROLLER_GOTO, {
                    detail: {
                        index: this.current,
                        behavior: "auto"
                    }
                })
            );
            this.attachFullscreenListeners();
            // defer pinchzoom activation to avoid issues in chrome during update of tile offsets
            setTimeout(() => {
                this.toggleAfter();
                this.zoomEnabled = true;
            }, 250);
        }
    }

    /**
     * attach fullscreen event listeners
     * @private
     */
    private attachFullscreenListeners() {
        this.abortController = new AbortController();
        const listenerOptions = { signal: this.abortController.signal };
        window.addEventListener("popstate", () => this.hide(), listenerOptions);
        this.btnClose.addEventListener("click", () => history.back(), listenerOptions);
        this.scroller?.addEventListener(constants.EVENTS.NAV_BEFORE, this.galleryNavHandler, listenerOptions);
        this.scroller?.addEventListener(constants.EVENTS.THUMB_BEFORE, this.galleryNavHandler, listenerOptions);
        this.zoomHandlers.forEach((zoomHandler) => {
            zoomHandler.element.addEventListener(
                zoomHandler.getOptionValue("animationStartEventName") as string,
                this.imageZoomedHandler,
                listenerOptions
            );
            zoomHandler.element.addEventListener(
                zoomHandler.getOptionValue("animationEndEventName") as string,
                this.imageZoomedHandler,
                listenerOptions
            );
        });
        this.scroller?.addEventListener(
            constants.EVENTS.SCROLL_START,
            () => (this.zoomEnabled = false),
            listenerOptions
        );
        this.scroller?.addEventListener(constants.EVENTS.SCROLL_END, () => (this.zoomEnabled = true), listenerOptions);
        window.addEventListener("resize", this.resizeHandler, listenerOptions);
    }

    /**
     * hide fullscreen gallery
     */
    public hide(): void {
        if (this.isFullscreen) {
            this.abortController.abort();
            this.zoomHandlers.forEach((zoomHandler) => {
                const figure = zoomHandler.element.closest<HTMLElement>("figure");
                const gallery = zoomHandler.element.closest<HTMLElement>(constants.SELECTORS.GALLERY);
                delete figure.dataset.zoomed;
                delete gallery.dataset.hasZoomed;
                zoomHandler.destroy();
            });
            this.zoomHandlers = [];
            this.viewportZoomable = true;
            if (this.nextSibling) {
                this.parent.insertBefore(this.node, this.nextSibling);
            } else {
                this.parent.appendChild(this.node);
            }
            this.toggleAfter();
            releaseScroll();
        }
    }

    private toggleAfter() {
        requestAnimationFrame(() => {
            this.scroller?.dispatchEvent(new CustomEvent(constants.EVENTS.SCROLLER_NAV_UPDATE));
            if (this.thumbScroller) {
                this.thumbScroller.dispatchEvent(new CustomEvent(constants.EVENTS.SCROLLER_NAV_UPDATE));
            }
            this.scroller?.dispatchEvent(
                new CustomEvent(constants.EVENTS.SCROLLER_GOTO, {
                    detail: {
                        index: this.current,
                        behavior: "auto"
                    }
                })
            );
        });
    }

    /**
     * handle image zoom animation events
     * @param e
     * @private
     */
    private handleImageZoomed(e: PinchZoomAnimationEvent): void {
        requestAnimationFrame(() => {
            const isStart = e.type === e.detail.handler.getOptionValue("animationStartEventName");
            const zoomed = e.detail.action !== "reset" && (e.detail.handler.zoomed || isStart);
            const figure = e.detail.handler.element.closest<HTMLElement>("figure");
            const gallery = e.detail.handler.element.closest<HTMLElement>(constants.SELECTORS.GALLERY);
            if (zoomed) {
                figure.dataset.zoomed = "";
                gallery.dataset.hasZoomed = "";
            } else {
                delete figure.dataset.zoomed;
                delete gallery.dataset.hasZoomed;
            }
        });
    }

    /**
     * handle gallery navigation events
     * @private
     */
    private handleGalleryNav() {
        const zoomHandler = this.zoomHandlers[this.current];
        if (zoomHandler.zoomed) {
            const detail: scrollerNavBeforeResultDetail = {
                deferNav: (zoomHandler.getOptionValue("animationDuration") as number) + 100
            };
            this.scroller.dispatchEvent(new CustomEvent(constants.EVENTS.NAV_BEFORE_RESULT, { detail }));
            zoomHandler.reset();
        }
    }

    /**
     * handle resize and orientation change events
     * @private
     */
    private handleResize() {
        const zoomHandler = this.zoomHandlers[this.current];
        zoomHandler.reset();
    }

    set zoomEnabled(v: boolean) {
        this.zoomHandlers[this.current][`${v ? "en" : "dis"}able`]();
    }

    get viewPortOptions(): string[] {
        const viewPortMeta = document.querySelector<HTMLMetaElement>(':root head meta[name="viewport"]');
        return viewPortMeta.content.split(",").map((c) => c.trim());
    }

    set viewPortOptions(options: string[]) {
        const viewPortMeta = document.querySelector<HTMLMetaElement>(':root head meta[name="viewport"]');
        viewPortMeta.content = options.join(", ");
    }

    set viewportZoomable(zoomable: boolean) {
        const viewportOptions = this.viewPortOptions;
        if (zoomable) {
            viewportOptions.splice(viewportOptions.indexOf("user-scalable=0"), 1);
            this.viewPortOptions = viewportOptions;
        } else {
            viewportOptions.push("user-scalable=0");
            this.viewPortOptions = viewportOptions;
        }
    }

    get isFullscreen(): boolean {
        return this.node.parentElement === document.body;
    }

    get current() {
        return parseInt(this.gallery.node.style.getPropertyValue("--current") || "0");
    }
}
